import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import ModernProductSelectorDialog from "../../utils/Selector/ModernProductSelectorDialog";
import { purchasesApi } from "../../../api/inventory/purchases";
import { isValidInventoryQuantity, normalizeInventoryQuantity } from "../../../pages/dashboard/inventory/inventory-quantity";
import { normalizeError } from "../../../operationFeedback/normalizeError";

const makeKey = () => typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

export function PurchaseLinesDialog({ open, purchaseId, purchaseVersion, supplierId, canViewCost, onClose, onSaved }) {
  const { t } = useTranslation();
  const unknownAddText = t("inventoryPurchase.unknownAdd");
  const companyId = useSelector((state) => state.session.currentCompany?.id);
  const pendingKey = `promang:purchase-lines:${companyId || "none"}:${purchaseId}:${supplierId || "none"}`;
  const [lines, setLines] = useState([]);
  const [requestKey, setRequestKey] = useState(makeKey);
  const [submitting, setSubmitting] = useState(false);
  const [unknown, setUnknown] = useState(false);
  const [error, setError] = useState(null);
  const pendingPayload = useRef(null);
  const pendingVersion = useRef(null);

  useEffect(() => {
    setLines([]);
    setRequestKey(makeKey());
    setUnknown(false);
    setError(null);
    pendingPayload.current = null;
    pendingVersion.current = null;
    if (!open) return;
    try {
      const saved = sessionStorage.getItem(pendingKey);
      if (!saved) return;
      const pending = JSON.parse(saved);
      if (!pending?.requestKey || !Array.isArray(pending?.payload) || !Array.isArray(pending?.lines)) return;
      setLines(pending.lines);
      setRequestKey(pending.requestKey);
      pendingPayload.current = pending.payload;
      pendingVersion.current = pending.version;
      setUnknown(true);
    } catch (_) { /* In-memory state remains usable if browser storage is unavailable. */ }
  }, [open, pendingKey]);

  useEffect(() => {
    if (unknown) setError(unknownAddText);
  }, [unknown, unknownAddText]);

  const selectProducts = (products) => {
    if (submitting || unknown) return;
    setError(null);
    setLines((current) => {
      const selectedById = new Map(products.map((product) => [String(product.id), product]));
      const kept = current.filter((line) => selectedById.has(String(line.product.id))).map((line) => {
        const product = selectedById.get(String(line.product.id));
        return { ...line, product: { ...line.product, ...product }, quantity: String(product.quantity ?? line.quantity), purchase_price: product.purchase_price == null ? "" : String(product.purchase_price) };
      });
      const byId = new Set(current.map((line) => String(line.product.id)));
      return [...kept, ...products.filter((product) => !byId.has(String(product.id))).map((product) => ({
        client_row_key: makeKey(), product,
        quantity: String(product.quantity ?? "1"),
        purchase_price: canViewCost && product.purchase_price != null ? String(product.purchase_price) : "",
      }))];
    });
  };

  const save = async () => {
    if (submitting || lines.length === 0) return;
    if (lines.some((line) => !isValidInventoryQuantity(line.quantity))) {
      setError(t("inventoryPurchase.quantityInvalid")); return;
    }
    const payload = pendingPayload.current || lines.map((line) => ({
      client_row_key: line.client_row_key,
      product_id: line.product.id,
      ...(line.product.supplier_product_id ? { supplier_product_id: line.product.supplier_product_id } : {}),
      quantity: normalizeInventoryQuantity(line.quantity),
      ...(canViewCost && line.purchase_price !== "" ? { purchase_price: line.purchase_price } : {}),
      unit_id: line.product.unit_id || null,
    }));
    const version = pendingVersion.current ?? purchaseVersion;
    setSubmitting(true); setError(null);
    try { sessionStorage.setItem(pendingKey, JSON.stringify({ requestKey, payload, lines, version })); } catch (_) { /* In-memory retry remains available. */ }
    try {
      const response = await purchasesApi.addItemsBulk(purchaseId, requestKey, payload, version);
      if (response?.data?.success !== true || !Array.isArray(response?.data?.data)) throw new Error("Invalid bulk result");
      pendingPayload.current = null;
      pendingVersion.current = null;
      try { sessionStorage.removeItem(pendingKey); } catch (_) { /* In-memory state was cleared. */ }
      onSaved(response.data.data, response.data.version);
      onClose();
    } catch (err) {
      const failure = normalizeError(err);
      // A later rejection cannot establish whether a previous timed-out request
      // committed. Keep its key, rows and version until success is confirmed.
      if (!pendingPayload.current && failure.httpStatus === 422 &&
          err?.response?.data?.success === false && failure.outcome === "not_applied" &&
          failure.code !== "request_key_reused") {
        setUnknown(false);
        setRequestKey(makeKey());
        pendingPayload.current = null;
        pendingVersion.current = null;
        try { sessionStorage.removeItem(pendingKey); } catch (_) { /* In-memory state was cleared. */ }
        setError(failure.safeMessage || t("inventoryPurchase.addFailed"));
      } else {
        // The server may have committed before a timeout. Keep the same key and payload.
        setUnknown(true);
        pendingPayload.current = payload;
        pendingVersion.current = version;
        setError(t("inventoryPurchase.unknownAdd"));
      }
    } finally { setSubmitting(false); }
  };

  return <ModernProductSelectorDialog
    open={open}
    onClose={() => { if (!submitting) onClose(); }}
    multiple
    selectedProducts={lines.map((line) => ({ ...line.product, quantity: line.quantity, purchase_price: line.purchase_price }))}
    onChange={selectProducts}
    onConfirmSelection={save}
    confirmSelectionLabel={submitting ? t("inventoryPurchase.saving") : unknown ? t("inventoryPurchase.retrySame") : t("inventoryPurchase.addSelected", { count: lines.length })}
    selectionHint={t("inventoryPurchase.addProductsHint")}
    selectionFeedback={error}
    selectionBusy={submitting}
    selectionLocked={submitting || unknown}
    quantityMin="0.0001"
    quantityStep="0.0001"
    showPurchasePriceInput={canViewCost}
    purchasePriceLabel={t("inventoryPurchase.price")}
    searchOnly
    hideCategoryAndSystemNav
    fetchProductPage={async (params) => {
      const response = await purchasesApi.searchSupplierProducts(supplierId, {
        query: params.query, page: params.page, per_page: params.per_page,
      });
      return { data: response.data.data, total_count: response.data.total_count };
    }}
  />;
}
